import { decode } from 'html-entities';
import { StyleSheet, View } from 'react-native';
import FastImage from '@d11/react-native-fast-image';
import { mvs } from 'react-native-size-matters';

import { useAppSelector } from '../../app/hooks';
import { COLORS, isTablet } from '../../constants';
import {
  selectDefaultCover,
  selectDivRadius,
} from '../../features/appconfig/store/selectors';
import { useAdjustedFontSize } from '../../hooks/useAdjustedFontSize';
import useOrientation from '../../hooks/useOrientation';
import { formatEpisodeDate } from '../../localization/datetime';
import CustomText from '../CustomText';

interface EpisodeHeaderProps {
  episodeImage: string;
  episodeTitle: string;
  episodeDate?: string;
}

export function EpisodeHeader({
  episodeImage,
  episodeTitle,
  episodeDate,
}: EpisodeHeaderProps) {
  const divRadius = useAppSelector(selectDivRadius);
  const defaultCover = useAppSelector(selectDefaultCover);

  // dynamic font sizes
  const episodeTitleFontSize = useAdjustedFontSize(mvs(20));
  const dateFontSize = useAdjustedFontSize(mvs(14));
  // tablet specific styles
  const orientation = useOrientation();
  const isTabletLandscape = isTablet && orientation === 'LANDSCAPE';

  return (
    <View style={styles.episodeHeader}>
      {/* episode image */}
      <View
        style={[
          styles.episodeImageContainer,
          { borderRadius: divRadius },
          isTabletLandscape && styles.episodeImageContainerLandscape,
        ]}
      >
        <FastImage
          source={episodeImage ? { uri: episodeImage } : defaultCover}
          style={styles.episodeImage}
          resizeMode={FastImage.resizeMode.cover}
        />
      </View>

      <View style={styles.dateAndEpisodeContainer}>
        {/* episode date */}
        {episodeDate && (
          <CustomText style={[styles.date, { fontSize: dateFontSize }]} weight="SemiBold">
            {formatEpisodeDate(episodeDate)}
          </CustomText>
        )}

        {/* episode title */}
        <CustomText
          style={[styles.episodeTitle, { fontSize: episodeTitleFontSize }]}
          weight="Bold"
          textAlign="center"
        >
          {decode(episodeTitle)}
        </CustomText>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  episodeHeader: {
    alignItems: 'center',
  },
  episodeImageContainer: {
    width: mvs(246),
    height: mvs(246),
    overflow: 'hidden',
  },
  episodeImageContainerLandscape: {
    width: mvs(180),
    height: mvs(180),
    overflow: 'hidden',
  },
  episodeImage: {
    width: '100%',
    height: '100%',
  },
  dateAndEpisodeContainer: {
    marginTop: mvs(32),
  },
  date: {
    marginBottom: mvs(4),
    opacity: 0.7,
    textAlign: 'center',
    color: COLORS.WHITE,
  },
  episodeTitle: {
    color: COLORS.WHITE,
    textAlign: 'center',
    lineHeight: mvs(26),
  },
});
